import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://trackmyfxea.com';
const DEFAULT_DESCRIPTION = 'Track MT4 and MT5 Expert Advisor performance with live account monitoring, EA analytics, drawdown tracking, and broker comparison.';

const PUBLIC_META = {
  '/': { title: 'TrackMyFxEA | MT4/MT5 EA Performance Analytics', description: DEFAULT_DESCRIPTION, type: 'SoftwareApplication' },
  '/home': { title: 'TrackMyFxEA | Know Which EA Makes You Money', description: DEFAULT_DESCRIPTION, type: 'SoftwareApplication' },
  '/features': { title: 'Features | TrackMyFxEA', description: 'Live EA tracking, detailed performance analysis, drawdown monitoring, reports, alerts, and broker comparison for MT4 and MT5.', type: 'SoftwareApplication' },
  '/explore': { title: 'Explore Verified EAs | TrackMyFxEA', description: 'Discover owner-published MT4 and MT5 EA profiles with connector-verified performance, drawdown and trading statistics.', type: 'CollectionPage' },
  '/how-it-works': { title: 'How TrackMyFxEA Works', description: 'Connect your MT4 or MT5 Expert Advisor and start receiving live trading performance analytics in minutes.', type: 'HowTo' },
  '/pricing': { title: 'Pricing | TrackMyFxEA', description: 'Choose the TrackMyFxEA plan that fits your MT4 and MT5 account monitoring needs.', type: 'Product' },
  '/ea-store': { title: 'EA Store | TrackMyFxEA', description: 'Browse Expert Advisors, platform details, result galleries, and tracking links from TrackMyFxEA.', type: 'CollectionPage' },
  '/privacy': { title: 'Privacy Policy | TrackMyFxEA', description: 'Read how TrackMyFxEA handles account and trading data.', type: 'WebPage' },
  '/terms': { title: 'Terms of Service | TrackMyFxEA', description: 'Read the TrackMyFxEA terms of service.', type: 'WebPage' },
  '/refund-policy': { title: 'Refund Policy | TrackMyFxEA', description: 'Read the TrackMyFxEA payment and refund policy.', type: 'WebPage' },
  '/risk-disclaimer': { title: 'Trading Risk Disclaimer | TrackMyFxEA', description: 'Read the TrackMyFxEA trading risk disclaimer.', type: 'WebPage' },
  '/contact': { title: 'Contact | TrackMyFxEA', description: 'Contact TrackMyFxEA support for account, EA connection, and billing help.', type: 'ContactPage' },
};

function upsertMeta(attribute, key, content) {
  if (!content) return;
  let node = document.head.querySelector('meta[' + attribute + '="' + key + '"]');
  if (!node) {
    node = document.createElement('meta');
    node.setAttribute(attribute, key);
    document.head.appendChild(node);
  }
  node.setAttribute('content', content);
}

function upsertLink(rel, href) {
  let node = document.head.querySelector('link[rel="' + rel + '"]');
  if (!node) {
    node = document.createElement('link');
    node.setAttribute('rel', rel);
    document.head.appendChild(node);
  }
  node.setAttribute('href', href);
}

export default function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    const isStoreDetail = path.startsWith('/ea-store/') && path !== '/ea-store/';
    const isPrivate = ['/dashboard', '/accounts', '/my-eas', '/magic-numbers', '/trades', '/reports', '/expenses', '/settings', '/admin', '/login', '/register', '/forgot-password', '/reset-password'].some((prefix) => path === prefix || path.startsWith(prefix + '/'));
    const meta = PUBLIC_META[path] || (isStoreDetail
      ? { title: 'Expert Advisor Details | TrackMyFxEA', description: 'Explore Expert Advisor details, platform compatibility, result images, and tracking information.', type: 'Product' }
      : { title: 'TrackMyFxEA', description: DEFAULT_DESCRIPTION, type: 'WebPage' });
    const canonical = SITE_URL + (path === '/' ? '/' : path.replace(/\/$/, ''));

    document.title = meta.title;
    upsertMeta('name', 'description', meta.description);
    upsertMeta('name', 'robots', isPrivate ? 'noindex, nofollow' : 'index, follow');
    upsertMeta('property', 'og:title', meta.title);
    upsertMeta('property', 'og:description', meta.description);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:type', meta.type === 'Product' ? 'product' : 'website');
    upsertMeta('property', 'og:site_name', 'TrackMyFxEA');
    upsertMeta('name', 'twitter:card', 'summary');
    upsertMeta('name', 'twitter:title', meta.title);
    upsertMeta('name', 'twitter:description', meta.description);
    upsertLink('canonical', canonical);

    const existing = document.getElementById('trackmyfxea-structured-data');
    if (existing) existing.remove();
    if (!isPrivate) {
      const script = document.createElement('script');
      script.id = 'trackmyfxea-structured-data';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': meta.type,
        name: meta.title,
        description: meta.description,
        url: canonical,
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        brand: { '@type': 'Brand', name: 'TrackMyFxEA' },
      });
      document.head.appendChild(script);
    }
  }, [location.pathname]);

  return null;
}
